import React, { useState, useEffect } from 'react';
import CertificateTemplateForm from '../certificate/CertificateTemplateForm';
import { documentTypesAPI } from '../../services/api';
import CertificatePreview from '../certificate/CertificatePreview';
import ConfirmModal from '../common/ConfirmModal';
import { showToast } from '../common/Toast';

import {
  PlusIcon
} from '@heroicons/react/24/outline';

// Print logic as a top-level helper
function handlePrint(tpl, sampleData) {
  let pageSizeCSS = '';
  switch (tpl.paperSize) {
    case 'A4':
      pageSizeCSS = '@page { size: A4; margin: 0; }';
      break;
    case 'Letter':
      pageSizeCSS = '@page { size: 8.5in 11in; margin: 0; }';
      break;
    case 'Legal':
      pageSizeCSS = '@page { size: 8.5in 14in; margin: 0; }';
      break;
    default:
      pageSizeCSS = '';
  }
  const bgUrl = tpl.backgroundImage ? `background-image: url('${tpl.backgroundImage}'); background-size: cover; background-position: center; background-repeat: no-repeat;` : '';
  const renderedHTML = renderTemplate(tpl.body, sampleData);
  const printContent = `
    <html>
      <head>
        <title>${tpl.name || 'Certificate'}</title>
        <style>
          ${pageSizeCSS}
          body { background: #fff; color: #222; font-family: Arial, sans-serif; font-size: 18px; margin: 0; padding: 32px; ${bgUrl} }
          .certificate-container { max-width: 800px; margin: 0 auto; background: transparent; }
          h1, h2, h3, h4, h5, h6 { text-align: center; }
          p { margin: 0.5em 0; }
        </style>
      </head>
      <body>
        <div class="certificate-container">
          ${renderedHTML}
        </div>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
    </html>
  `;
  const printWindow = window.open('', '_blank', 'width=900,height=1200');
  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();

  // Parent window: polling to close printWindow after print dialog closes
  printWindow.focus();
  const poll = setInterval(() => {
    if (printWindow.closed) {
      clearInterval(poll);
    } else if (document.hasFocus()) {
      // If parent regains focus, close print window
      printWindow.close();
      clearInterval(poll);
    }
  }, 500);
}
import { renderTemplate } from '../../utils/templateRenderer';
import { certificateTemplatesAPI } from '../../services/api';


// Helper to get ordinal suffix for a day
function getDayWithSuffix(day) {
  if (day > 3 && day < 21) return day + 'th';
  switch (day % 10) {
    case 1: return day + 'st';
    case 2: return day + 'nd';
    case 3: return day + 'rd';
    default: return day + 'th';
  }
}

function formatLongDate(dateObj) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const day = getDayWithSuffix(dateObj.getDate());
  const month = months[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  return `${day} day of ${month}, ${year}`;
}

// Default sample data for preview
const sampleData = {
  resident_name: 'Juan Dela Cruz',
  address: '123 Main St',
  age: 28,
  civil_status: 'Single',
  gender: 'Male',
  pronouns: 'He/She',
  birthdate: 'January 1, 1997',
  birthplace: 'Manila',
  nationality: 'Filipino',
  occupation: 'Engineer',
  purpose: 'Employment',
  issued_by: 'Barangay Captain',
  date: formatLongDate(new Date()),
  tracking_number: 'ABC-2025-00001',
  fee_amount: '₱ 100.00',
};

// This state will hold the actual request data when a document is being requested
// In a real app, this would come from props, context, or a fetch from backend
// For demo, you can set it to null or to a mock object

const CertificateTemplatesPage = ({ requestData = null }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);


  // Use requestData if present, otherwise fallback to sampleData
  const previewData = requestData || sampleData;

  // When a request is present, auto-select the template for its document type
  useEffect(() => {
    if (requestData && templates.length > 0) {
      const match = templates.find(tpl => tpl.document_type_key === requestData.type || tpl.document_type_key === requestData.document_type_key);
      if (match) setSelected(match);
    }
  }, [requestData, templates]);


  // Load templates and document types from backend
  useEffect(() => {
    fetchTemplates();
    fetchDocumentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await certificateTemplatesAPI.getAll();
      setTemplates(data);
      // Auto-select first template if none selected and no requestData
      if (data.length > 0 && !selected && !requestData) {
        setSelected(data[0]);
      }
    } catch {
      setTemplates([]);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      const { data } = await documentTypesAPI.getAllV1();
      // If API response is {success: true, data: [...]}, use data.data
      const types = Array.isArray(data) ? data : (Array.isArray(data.data) ? data.data : []);
      setDocumentTypes(types);
      console.log('Fetched documentTypes:', types);
    } catch {
      setDocumentTypes([]);
      console.log('Failed to fetch documentTypes');
    }
  };


  const handleAdd = async () => {
    await fetchDocumentTypes();
    console.log('documentTypes in handleAdd:', documentTypes);
    setEditData(null);
    setShowForm(true);
  };

  const handleEdit = async (tpl) => {
    await fetchDocumentTypes();
    console.log('documentTypes in handleEdit:', documentTypes);
    setEditData(tpl);
    setShowForm(true);
  };

  // const handleDelete = async (id) => {
  //   if (window.confirm('Delete this template?')) {
  //     await certificateTemplatesAPI.delete(id);
  //     fetchTemplates();
  //     if (selected && selected.id === id) setSelected(null);
  //   }
  // };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    try {
      const response = await certificateTemplatesAPI.delete(deleteId);
      showToast('Template deleted successfully!', 'success');

      // Refresh templates and update selected if needed
      await fetchTemplates();
      if (selected && selected.id === deleteId) setSelected(null);

      // Close modal immediately since toast shows the feedback
      setDeleteId(null);
    } catch (error) {
      // Handle error response from backend
      const errorMessage = error.response?.data?.message || 'Failed to delete template. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setDeleteLoading(false);
    }
  };


  // When creating/updating a template, require document_type_key
  const handleFormSubmit = async (tpl) => {
    if (!tpl.document_type_id) {
      alert('Please select a document type for this template.');
      return;
    }
    const payload = {
      ...tpl,
      document_type_id: tpl.document_type_id,
      body: tpl.body
    };
    if (editData) {
      await certificateTemplatesAPI.update(tpl.id, payload);
    } else {
      await certificateTemplatesAPI.create(payload);
    }
    // Refresh templates and update selected if needed
    const { data } = await certificateTemplatesAPI.getAll();
    setTemplates(data);
    // If editing the selected template, update selected to the new version
    if (selected && tpl.id === selected.id) {
      const updated = data.find(t => t.id === tpl.id);
      if (updated) setSelected(updated);
    }
    setShowForm(false);
    setEditData(null);
  };

  // Print logic now uses previewData (real request data if present)
  const handlePrint = (tpl, data = previewData) => {
    let pageSizeCSS = '';
    switch (tpl.paperSize) {
      case 'A4':
        pageSizeCSS = '@page { size: A4; margin: 0; }';
        break;
      case 'Letter':
        pageSizeCSS = '@page { size: 8.5in 11in; margin: 0; }';
        break;
      case 'Legal':
        pageSizeCSS = '@page { size: 8.5in 14in; margin: 0; }';
        break;
      default:
        pageSizeCSS = '';
    }
    const bgUrl = tpl.backgroundImage ? `background-image: url('${tpl.backgroundImage}'); background-size: cover; background-position: center; background-repeat: no-repeat;` : '';
    const renderedHTML = renderTemplate(tpl.body, data);
    const printContent = `
      <html>
        <head>
          <title>${tpl.name || 'Certificate'}</title>
          <style>
            ${pageSizeCSS}
            body { background: #fff; color: #222; font-family: Arial, sans-serif; margin: 0; padding: 32px; ${bgUrl} }
            .certificate-container { max-width: 800px; margin: 0 auto; background: transparent; }
            h1, h2, h3, h4, h5, h6 { text-align: center; }
            p { margin: 0.5em 0; }
          </style>
        </head>
        <body>
          <div class="certificate-container">
            ${renderedHTML}
          </div>
          <script>window.onload = function() { window.print(); window.onafterprint = window.close; };</script>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <div className="w-100">

      <div className="flex flex-col md:flex-row gap-2">
        <div className="md:w-2/3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="font-semibold mb-2">Templates</h2>
            <button
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md mb-3 shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95"
              onClick={handleAdd}
            >
              <PlusIcon className="w-4 h-4 mr-2" />Add Certificate Template
            </button>
          </div>

          <ul className="divide-y">
            {templates.map(tpl => (
              <li
                key={tpl.id}
                className={`py-2 px-2 flex items-center justify-between rounded transition-colors duration-150 group ${selected?.id === tpl.id ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
              >
                <span
                  className={`flex-1 cursor-pointer truncate ${selected?.id === tpl.id ? ' text-blue-700' : 'text-gray-800 group-hover:text-blue-600'}`}
                  title={tpl.certificate_name}
                  onClick={() => setSelected(tpl)}
                >
                  {tpl.certificate_name}
                </span>
                <div className="flex items-center gap-1 ml-2 opacity-80 group-hover:opacity-100">
                  <button
                    className="p-1 rounded hover:bg-green-100 text-green-700 transition-colors"
                    title="Edit"
                    onClick={() => handleEdit(tpl)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="inline w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a4 4 0 01-1.414.828l-4.243 1.414 1.414-4.243a4 4 0 01.828-1.414z" /></svg>
                  </button>
                  <button
                    className="p-1 rounded hover:bg-red-100 text-red-700 transition-colors"
                    title="Delete"
                    // onClick={() => handleDelete(tpl.id)}
                    onClick={() => setDeleteId(tpl.id)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="inline w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>

                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:w-10/12 w-full flex flex-col items-center overflow-y-auto h-[calc(100vh-200px)] p-2" >
          <div
            className="certificate-preview-a4 bg-white shadow border border-gray-200 rounded-lg w-full"
            style={{
              minHeight: '200mm',
              maxWidth: '210mm',
              width: '100%',
              margin: '0 auto',
              background: 'white',
              boxShadow: '0 0 8px 0 rgba(0,0,0,0.04)',
              justifyContent: 'flex-start'
            }}
          >
            {selected ? (
              <CertificatePreview template={selected} data={previewData} showPrint={false} />
            ) : (
              <div className="text-gray-500 flex justify-center items-center">Select a template to preview</div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <CertificateTemplateForm
          initialData={editData
            ? {
              ...editData,
              document_type_label: documentTypes.find(dt => dt.id === editData.document_type_id)?.label || '',
              document_type_id: editData.document_type_id
            }
            : {
              document_type_id: '',
              body: '',
            }
          }
          documentTypes={documentTypes}
          onSubmit={handleFormSubmit}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onDocumentTypeAdded={async (newType) => {
            // Refresh document types list when a new one is added
            await fetchDocumentTypes();
          }}
        />
      )}

      <ConfirmModal
        open={!!deleteId}
        title="Delete this template?"
        message="Are you sure you want to delete this certificate template? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default React.memo(CertificateTemplatesPage);
